import { DtcTypes } from '@eagle/data-function-types';
import type { EthingsRestClient } from '@eagle/ethings-rest-client';
import { GetThingsFaultStatusInput, GetThingsFaultStatusResult, ThingFaultStatus } from '../types/thing-fault-status';
import { WarningValue } from '../types/thing-fault-summary';
import { buildFaultGroupKey, calculateDateRange, isLampLit } from './thing-fault-summary';

const DEFAULT_RANGE_DAYS = 30;
const DEFAULT_BATCH_SIZE = 100;
const DEFAULT_BATCH_CONCURRENCY = 2;
const THING_EVENT_LIMIT = 500;

const normalizePositiveInteger = (value: number | undefined, fallback: number): number => {
  return value && Number.isInteger(value) && value > 0 ? value : fallback;
};

interface FleetFaultEvent {
  eventId: string;
  eventData: Record<string, unknown>;
  thingId: string;
  occurredAt: string;
  active: boolean | null;
  protocol: string;
  sourceAddress: string | number | null;
  code: string | null;
  type: DtcTypes;
  failureModeIdentifier: number | null;
  warning: WarningValue;
}

interface ThingEventQueryResponse {
  items?: unknown[];
  hasMore?: boolean;
  count?: number;
}

interface ThingEventClient {
  getAllV2?: (params: Record<string, unknown>, requestOptions?: { signal?: AbortSignal; chunkSize?: number }) => Promise<ThingEventQueryResponse>;
}

interface BatchFetchResult {
  events: FleetFaultEvent[];
  pageCount: number;
  eventCount: number;
  complete: boolean;
}

interface FaultState {
  eventId: string;
  thingId: string;
  protocol: string;
  sourceAddress: string | number | null;
  code: string;
  type: DtcTypes;
  failureModeIdentifier: number | null;
  active: boolean | null;
  warning: WarningValue;
  activeSince?: string;
  lastSeenAt: string;
  latestOccurredAt: string;
}

const warningPriority: Record<WarningValue, number> = {
  [WarningValue.RedStop]: 0,
  [WarningValue.Protect]: 1,
  [WarningValue.Amber]: 2,
  [WarningValue.None]: 3,
  [WarningValue.Unknown]: 4,
};

const getThingEventClient = (restClient: EthingsRestClient): ThingEventClient => {
  const unknownClient = restClient as unknown as Record<string, unknown>;
  const thingEvent = unknownClient.thingEvent;
  if (!thingEvent || typeof thingEvent !== 'object') {
    throw new Error('EthingsRestClient does not expose thingEvent client');
  }

  return thingEvent as ThingEventClient;
};

const withConcurrency = async <TInput, TResult>(
  items: TInput[],
  concurrency: number,
  handler: (item: TInput) => Promise<TResult>,
): Promise<TResult[]> => {
  if (items.length === 0) return [];

  const boundedConcurrency = Math.max(1, Math.min(concurrency, items.length));
  const results = new Array<TResult>(items.length);
  let index = 0;

  const worker = (): Promise<void> => {
    if (index >= items.length) {
      return Promise.resolve();
    }

    const currentIndex = index;
    index += 1;

    return handler(items[currentIndex]).then((result) => {
      results[currentIndex] = result;
      return worker();
    });
  };

  await Promise.all(Array.from({ length: boundedConcurrency }, () => worker()));
  return results;
};

const chunkArray = <T>(items: T[], batchSize: number): T[][] => {
  if (items.length === 0) return [];

  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += batchSize) {
    chunks.push(items.slice(i, i + batchSize));
  }
  return chunks;
};

const toStringOrNull = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (value instanceof Date && !isNaN(value.getTime())) {
    return value.toISOString();
  }

  return null;
};

const toNumberOrNull = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const normalizeWarning = (data: Record<string, unknown>, active: boolean | null): WarningValue => {
  if (isLampLit(data.redStop)) return WarningValue.RedStop;
  if (isLampLit(data.protect)) return WarningValue.Protect;
  if (isLampLit(data.amberWarning)) return WarningValue.Amber;
  if (active === null) return WarningValue.Unknown;
  return WarningValue.None;
};

const normalizeFleetFaultEvent = (rawEvent: unknown): FleetFaultEvent | null => {
  if (!rawEvent || typeof rawEvent !== 'object') return null;

  const event = rawEvent as Record<string, unknown>;
  const thingId = toStringOrNull(event.thingId);
  const occurredAt = toStringOrNull(event.occurred);
  const eventId = toStringOrNull(event.eventId);
  const data = event.data;

  if (!thingId || !occurredAt || !eventId || !data || typeof data !== 'object') {
    return null;
  }

  const eventData = data as Record<string, unknown>;
  const activeRaw = eventData.active;
  const active = typeof activeRaw === 'boolean' ? activeRaw : null;
  const code = toStringOrNull(eventData.code);
  const protocol = toStringOrNull(eventData.protocol) ?? '';
  const sourceAddressRaw = eventData.sourceAddress;
  const sourceAddress = typeof sourceAddressRaw === 'string' || typeof sourceAddressRaw === 'number'
    ? sourceAddressRaw
    : null;
  const failureModeIdentifier = toNumberOrNull(eventData.failureModeIdentifier ?? eventData.fmi);

  return {
    eventId,
    eventData,
    thingId,
    occurredAt,
    active,
    protocol,
    sourceAddress,
    code,
    type: eventData.type as DtcTypes,
    failureModeIdentifier,
    warning: normalizeWarning(eventData, active),
  };
};

const assertNotAborted = (signal?: AbortSignal): void => {
  if (signal?.aborted) {
    throw new DOMException('Fault status request was aborted', 'AbortError');
  }
};

const fetchAllThingEventsForBatch = async (
  restClient: EthingsRestClient,
  thingIds: string[],
  dateRangeStart: string,
  limit: number,
  signal?: AbortSignal,
): Promise<BatchFetchResult> => {
  const thingEventClient = getThingEventClient(restClient);
  const getAllV2 = thingEventClient.getAllV2;

  if (!getAllV2) {
    throw new Error('EthingsRestClient thingEvent.getAllV2 is not available for multi-Thing queries');
  }

  const events: FleetFaultEvent[] = [];
  const fetchPage = async (skip: number, pageCount: number): Promise<number> => {
    assertNotAborted(signal);

    const response = await getAllV2({
      dateRangeStart,
      limit,
      skip,
      filter: {
        eventTypeId: { $in: ['update'] },
        feature: { $in: ['fault/dtc'] },
        thingId: { $in: thingIds },
      },
    }, { signal, chunkSize: limit }); // chunkSize sets actual page size; params.limit alone defaults to 100

    const nextPageCount = pageCount + 1;

    const rawItems = Array.isArray(response.items) ? response.items : [];
    const pageItems = rawItems
      .map(normalizeFleetFaultEvent)
      .filter((event): event is FleetFaultEvent => event !== null);

    events.push(...pageItems);

    const hasMore = Boolean(response.hasMore);
    if (!hasMore) return nextPageCount;

    if (rawItems.length === 0) {
      throw new Error('thing-event pagination did not complete: empty page while hasMore=true');
    }

    return fetchPage(skip + rawItems.length, nextPageCount);
  };

  const pageCount = await fetchPage(0, 0);

  return {
    events,
    pageCount,
    eventCount: events.length,
    complete: true,
  };
};

const getHighestWarning = (warnings: WarningValue[]): WarningValue => {
  if (!warnings.length) return WarningValue.None;

  return [...warnings].sort((a, b) => warningPriority[a] - warningPriority[b])[0];
};

const toFaultStateMap = (events: FleetFaultEvent[]): Map<string, FaultState> => {
  const grouped = new Map<string, FleetFaultEvent[]>();

  for (const event of events) {
    const key = buildFaultGroupKey(
      event.thingId,
      event.protocol,
      String(event.sourceAddress ?? ''),
      event.code ?? '',
      String(event.failureModeIdentifier ?? ''),
    );

    const existing = grouped.get(key);
    if (existing) {
      existing.push(event);
    } else {
      grouped.set(key, [event]);
    }
  }

  const stateMap = new Map<string, FaultState>();

  for (const [key, groupedEvents] of grouped.entries()) {
    const sorted = [...groupedEvents].sort((a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime());
    const latestWithState = [...sorted].reverse().find((event) => typeof event.active === 'boolean') ?? null;
    const firstActive = sorted.find((event) => event.active === true);
    const lastEvent = sorted[sorted.length - 1];
    const reference = latestWithState ?? lastEvent;

    stateMap.set(key, {
      eventId: reference.eventId,
      thingId: reference.thingId,
      protocol: reference.protocol,
      sourceAddress: reference.sourceAddress,
      code: reference.code ?? '',
      type: reference.type,
      failureModeIdentifier: reference.failureModeIdentifier,
      active: latestWithState?.active ?? null,
      warning: reference.warning,
      activeSince: firstActive?.occurredAt,
      lastSeenAt: lastEvent.occurredAt,
      latestOccurredAt: reference.occurredAt,
    });
  }

  return stateMap;
};

const normalizeThingIds = (thingIds: string[]): string[] => {
  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const thingId of thingIds) {
    const id = thingId.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    normalized.push(id);
  }

  return normalized;
};

const toEmptyResult = (thingIds: string[], rangeDays: number, rangeStart: string, rangeEnd: string): GetThingsFaultStatusResult => {
  const items: ThingFaultStatus[] = thingIds.map((thingId) => ({
    thingId,
    status: 'no-faults',
    activeFaultCount: 0,
    totalFaultCount: 0,
    warning: {
      currentHighest: WarningValue.None,
    },
    activeFaults: [],
  }));

  return {
    items,
    range: {
      days: rangeDays,
      start: rangeStart,
      end: rangeEnd,
    },
    meta: {
      requestedThingCount: thingIds.length,
      batchCount: 0,
      pageCount: 0,
      eventCount: 0,
      complete: true,
      generatedAt: new Date().toISOString(),
    },
  };
};

export const getThingsFaultStatus = async (
  restClient: EthingsRestClient,
  input: GetThingsFaultStatusInput,
): Promise<GetThingsFaultStatusResult> => {
  const rangeDays = normalizePositiveInteger(input.range, DEFAULT_RANGE_DAYS);
  const batchSize = normalizePositiveInteger(input.batchSize, DEFAULT_BATCH_SIZE);
  const concurrency = normalizePositiveInteger(input.concurrency, DEFAULT_BATCH_CONCURRENCY);
  const limit = normalizePositiveInteger(input.limit, THING_EVENT_LIMIT);
  const normalizedThingIds = normalizeThingIds(input.thingIds);
  const dateRange = calculateDateRange(rangeDays);

  if (!normalizedThingIds.length) {
    return toEmptyResult([], rangeDays, dateRange.start, dateRange.end);
  }

  const batches = chunkArray(normalizedThingIds, Math.max(1, batchSize));

  const batchResults = await withConcurrency(
    batches,
    concurrency,
    (thingIdsBatch) => fetchAllThingEventsForBatch(restClient, thingIdsBatch, dateRange.start, limit, input.signal),
  );

  const allEvents = batchResults.flatMap((result) => result.events);
  const stateMap = toFaultStateMap(allEvents);

  const statesByThing = new Map<string, FaultState[]>();
  for (const state of stateMap.values()) {
    const existing = statesByThing.get(state.thingId);
    if (existing) {
      existing.push(state);
    } else {
      statesByThing.set(state.thingId, [state]);
    }
  }

  const items: ThingFaultStatus[] = normalizedThingIds.map((thingId) => {
    const thingStates = statesByThing.get(thingId) ?? [];
    if (!thingStates.length) {
      return {
        thingId,
        status: 'no-faults',
        activeFaultCount: 0,
        totalFaultCount: 0,
        warning: {
          currentHighest: WarningValue.None,
        },
        activeFaults: [],
      };
    }

    const activeStates = thingStates.filter((state) => state.active === true);
    const knownStatesCount = thingStates.filter((state) => typeof state.active === 'boolean').length;
    const status: ThingFaultStatus['status'] = activeStates.length
      ? 'active'
      : knownStatesCount > 0
        ? 'cleared'
        : 'unknown';

    const latestFaultOccurredAt = [...thingStates]
      .sort((a, b) => new Date(b.latestOccurredAt).getTime() - new Date(a.latestOccurredAt).getTime())[0]?.latestOccurredAt;

    const activeFaults = activeStates
      .map((state) => ({
        id: buildFaultGroupKey(
          state.thingId,
          state.protocol,
          String(state.sourceAddress ?? ''),
          state.code,
          String(state.failureModeIdentifier ?? ''),
        ),
        eventId: state.eventId,
        code: state.code,
        type: state.type,
        sourceAddress: state.sourceAddress,
        failureModeIdentifier: state.failureModeIdentifier,
        warning: state.warning,
        activeSince: state.activeSince,
        lastSeenAt: state.lastSeenAt,
      }))
      .sort((a, b) => warningPriority[a.warning] - warningPriority[b.warning]);

    return {
      thingId,
      status,
      activeFaultCount: activeStates.length,
      totalFaultCount: thingStates.length,
      warning: {
        currentHighest: getHighestWarning(activeStates.map((state) => state.warning)),
      },
      latestFaultOccurredAt,
      activeFaults,
    };
  });

  return {
    items,
    range: {
      days: rangeDays,
      start: dateRange.start,
      end: dateRange.end,
    },
    meta: {
      requestedThingCount: normalizedThingIds.length,
      batchCount: batches.length,
      pageCount: batchResults.reduce((sum, result) => sum + result.pageCount, 0),
      eventCount: batchResults.reduce((sum, result) => sum + result.eventCount, 0),
      complete: batchResults.every((result) => result.complete),
      generatedAt: new Date().toISOString(),
    },
  };
};
